import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getTypecodesByType } from '../api/dms/typecode.api';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { ITypeCode } from '../models/dms/typecode.model';
import { LogType } from '../models/log.model';
import { setIsLoadingCountState } from './appSlice';

interface ITypecodeState {
  zrts?: ITypeCode[];
  zrtsLoading: boolean;
  ms?: ITypeCode[];
  msLoading: boolean;
  ztaskrr?: ITypeCode[];
  ztaskrrLoading: boolean;
  ztaskcm?: ITypeCode[];
  ztaskcmLoading: boolean;
}

const initialState: ITypecodeState = {
  zrts: undefined,
  zrtsLoading: false,
  ms: undefined,
  msLoading: false,
  ztaskrr: undefined,
  ztaskrrLoading: false,
  ztaskcm: undefined,
  ztaskcmLoading: false,
};

/// THUNKS

export const fetchZrtsTypecodesAsync = createAsyncThunk<{}, void>(
  'typecode/fetchZrtsTypecodesAsync',
  async (e, { rejectWithValue, dispatch }) => {
    // Road to sale typecode
    logTransaction(LogType.Info, `Fetching typecode "ZRTS" from database`, '[typecode] fetchZrtsTypecodesAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setZrtsLoadingState(true));

      const responseData = await getTypecodesByType('zrts');

      // Remove the typecode header
      const items = responseData.items.slice(1);

      dispatch(setZrtsState(items));
      dispatch(setIsLoadingCountState(-1));
      dispatch(setZrtsLoadingState(false));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setZrtsLoadingState(false));
    }
  }
);

export const fetchMsTypecodesAsync = createAsyncThunk<{}, void>(
  'typecode/fetchMsTypecodesAsync',
  async (e, { rejectWithValue, dispatch }) => {
    // Marital status typecode
    logTransaction(LogType.Info, `Fetching typecode "MS" from database`, '[typecode] fetchMsTypecodesAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setMsLoadingState(true));

      const responseData = await getTypecodesByType('ms');

      // Remove the typecode header
      const items = responseData.items.slice(1);

      dispatch(setMsState(items));
      dispatch(setIsLoadingCountState(-1));
      dispatch(setMsLoadingState(false));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setMsLoadingState(false));
    }
  }
);

export const fetchZtaskrrTypecodesAsync = createAsyncThunk<{}, void>(
  'typecode/fetchZtaskrrTypecodesAsync',
  async (e, { rejectWithValue, dispatch }) => {
    logTransaction(LogType.Info, `Fetching typecode "ZTASKRR" from database`, '[typecode] fetchZtaskrrTypecodesAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setZtaskrrLoadingState(true));

      const responseData = await getTypecodesByType('ztaskrr');

      // Remove the typecode header
      const items = responseData.items.slice(1);

      dispatch(setZtaskrrState(items));
      dispatch(setIsLoadingCountState(-1));
      dispatch(setZtaskrrLoadingState(false));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setZtaskrrLoadingState(false));
    }
  }
);

export const fetchZtaskcmTypecodesAsync = createAsyncThunk<{}, void>(
  'typecode/fetchZtaskcmTypecodesAsync',
  async (e, { rejectWithValue, dispatch }) => {
    logTransaction(LogType.Info, `Fetching typecode "ZTASKCM" from database`, '[typecode] fetchZtaskcmTypecodesAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setZtaskcmLoadingState(true));

      const responseData = await getTypecodesByType('ztaskcm');

      // Remove the typecode header
      const items = responseData.items.slice(1);

      dispatch(setZtaskcmState(items));
      dispatch(setIsLoadingCountState(-1));
      dispatch(setZtaskcmLoadingState(false));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setZtaskcmLoadingState(false));
    }
  }
);

/// SLICE

export const typecodeSlice = createSlice({
  name: 'typecode',
  initialState,
  reducers: {
    setZrtsState: (state, action: PayloadAction<ITypeCode[]>) => {
      logConsole(LogType.Info, '[typecode] setZrtsState');
      state.zrts = action.payload;
    },
    setZrtsLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[typecode] setZrtsLoadingState: ${action.payload}`);
      state.zrtsLoading = action.payload;
    },
    setMsState: (state, action: PayloadAction<ITypeCode[]>) => {
      logConsole(LogType.Info, '[typecode] setMsState');
      state.ms = action.payload;
    },
    setMsLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[typecode] setMsLoadingState: ${action.payload}`);
      state.msLoading = action.payload;
    },
    setZtaskrrState: (state, action: PayloadAction<ITypeCode[]>) => {
      logConsole(LogType.Info, '[typecode] setZtaskrrState');
      state.ztaskrr = action.payload;
    },
    setZtaskrrLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[typecode] setZtaskrrLoadingState: ${action.payload}`);
      state.ztaskrrLoading = action.payload;
    },
    setZtaskcmState: (state, action: PayloadAction<ITypeCode[]>) => {
      logConsole(LogType.Info, '[typecode] setZtaskcmState');
      state.ztaskcm = action.payload;
    },
    setZtaskcmLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[typecode] setZtaskcmLoadingState: ${action.payload}`);
      state.ztaskcmLoading = action.payload;
    },
  }
});

/// REDUCERS

export const {
  setZrtsState,
  setZrtsLoadingState,
  setMsState,
  setMsLoadingState,
  setZtaskrrState,
  setZtaskrrLoadingState,
  setZtaskcmState,
  setZtaskcmLoadingState,
} = typecodeSlice.actions;

/// CUSTOM SELECTORS

export default typecodeSlice.reducer;
